/* ============================================
   COMPXSS — Shopify theme behaviour
   Every component initialises from server-rendered markup, so content lives
   in the theme editor (section + block settings) instead of in this file.
   Each init runs on page load and again on shopify:section:load.
   ============================================ */

(() => {
  const coarse = () => window.matchMedia('(hover: none)').matches;
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const COMPASS_ARROW = '<svg width="100%" height="100%" viewBox="0 0 48 48" fill="currentColor" stroke="currentColor" stroke-width="5.5" stroke-linejoin="round" stroke-linecap="round"><path d="M24 10 L40 38 L24 31 L8 38 Z"/></svg>';

  /* ---------- Header ---------- */
  function initHeader(root) {
    const header = root.querySelector('.site-header');
    if (!header) return;
    const onScroll = () => header.classList.toggle('scrolled', window.scrollY > 20);
    if (!header.dataset.bound) {
      header.dataset.bound = '1';
      window.addEventListener('scroll', onScroll, { passive: true });
    }
    onScroll();

    const toggle = header.querySelector('[data-mobile-toggle]');
    const links = header.querySelector('.nav-links');
    if (toggle && links) {
      toggle.addEventListener('click', () => {
        const open = links.classList.toggle('open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    const dateEl = header.querySelector('[data-nav-date]');
    if (dateEl) {
      const d = new Date();
      const day = d.toLocaleDateString('en-US', { weekday: 'short' });
      const mo = d.toLocaleDateString('en-US', { month: 'short' });
      dateEl.textContent = `${day} · ${mo} ${d.getDate()}`;
    }
  }

  /* ---------- Compass + full-screen pillar takeover ---------- */
  const TAKEOVER_ID = 'cxsCompassTakeover';

  function ensureTakeover() {
    let el = document.getElementById(TAKEOVER_ID);
    if (el) return el;
    el = document.createElement('div');
    el.id = TAKEOVER_ID;
    el.className = 'compass-takeover';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = `
      <div class="compass-takeover-media">
        <img class="ct-img" alt="" aria-hidden="true">
        <div class="ct-scrim"></div>
      </div>
      <button class="ct-close" aria-label="Close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M6 6l12 12M18 6L6 18"/></svg>
      </button>
      <div class="container ct-inner">
        <div class="ct-eyebrow"></div>
        <h2 class="ct-concept"></h2>
        <div class="ct-tagline"></div>
        <p class="ct-blurb"></p>
        <div class="ct-tags"></div>
        <a class="ct-enter btn btn-light btn-lg" href="#"></a>
      </div>`;
    document.body.appendChild(el);
    el.querySelector('.ct-close').addEventListener('click', closeTakeover);
    el.addEventListener('click', (e) => {
      if (e.target === el || e.target.classList.contains('ct-scrim')) closeTakeover();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && el.classList.contains('is-open')) closeTakeover();
    });
    return el;
  }

  function showTakeover(btn, host) {
    const el = ensureTakeover();
    const d = btn.dataset;
    el.className = `compass-takeover q-${d.dir}`;
    el.querySelector('.ct-img').src = d.image || '';
    el.querySelector('.ct-eyebrow').textContent = host.dataset.takeoverEyebrow || '';
    el.querySelector('.ct-concept').textContent = d.concept || '';
    el.querySelector('.ct-tagline').textContent = d.tagline || '';
    el.querySelector('.ct-blurb').textContent = d.blurb || '';
    const tags = el.querySelector('.ct-tags');
    tags.textContent = '';
    (d.tags || '').split(',').map(t => t.trim()).filter(Boolean).forEach(t => {
      const s = document.createElement('span');
      s.textContent = t;
      tags.appendChild(s);
    });
    const enter = el.querySelector('.ct-enter');
    enter.href = d.url || '#';
    enter.textContent = host.dataset.enterLabel || '';
    void el.offsetWidth;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
  }

  function closeTakeover() {
    const el = document.getElementById(TAKEOVER_ID);
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
  }

  function initCompass(root) {
    root.querySelectorAll('[data-compass]').forEach(host => {
      const hint = host.querySelector('[data-compass-hint]');
      if (hint) hint.textContent = coarse() ? host.dataset.hintTouch : host.dataset.hintPointer;
      host.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-dir]');
        if (!btn) return;
        if (coarse() || designMode) { e.preventDefault(); showTakeover(btn, host); }
        else if (btn.dataset.url) window.location.href = btn.dataset.url;
      });
      if (!coarse()) {
        host.addEventListener('mouseover', (e) => {
          const btn = e.target.closest('[data-dir]');
          if (btn) showTakeover(btn, host);
        });
        host.addEventListener('mouseleave', closeTakeover);
        host.addEventListener('focusin', (e) => {
          const btn = e.target.closest('[data-dir]');
          if (btn) showTakeover(btn, host);
        });
      }
    });
  }

  /* ---------- Classes: pillar filter + cinematic panels ---------- */
  function initClasses(root) {
    root.querySelectorAll('[data-classes]').forEach(host => {
      document.body.classList.add('classes-dark', 'cx-classes');
      const panels = Array.from(host.querySelectorAll('.cx-panel'));
      const filters = host.querySelector('.cx-filters');
      const count = host.querySelector('[data-cx-count]');
      const emptyAll = host.querySelector('[data-cx-empty="all"]');
      const emptyPillar = host.querySelector('[data-cx-empty="pillar"]');
      const one = host.dataset.labelOne || 'class';
      const many = host.dataset.labelMany || 'classes';
      const valid = ['north', 'south', 'east', 'west'];
      let active = 'all';
      const h = location.hash.replace('#', '');
      if (valid.includes(h)) active = h;

      panels.forEach(p => {
        p.querySelectorAll('.cx-arrow').forEach(a => { if (!a.innerHTML.trim()) a.innerHTML = COMPASS_ARROW; });
      });

      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('is-in'); });
      }, { threshold: 0.25 });

      function render() {
        let shown = 0;
        let first = null;
        panels.forEach((p, i) => {
          const on = active === 'all' || p.dataset.quadrant === active;
          p.hidden = !on;
          p.classList.remove('is-first');
          if (on) {
            p.dataset.side = shown % 2 === 0 ? 'left' : 'right';
            if (!first) first = p;
            shown++;
          }
        });
        if (first) first.classList.add('is-first');
        if (count) count.textContent = `${shown} ${shown === 1 ? one : many}`;
        if (emptyAll) emptyAll.hidden = !(shown === 0 && (active === 'all' || panels.length === 0));
        if (emptyPillar) emptyPillar.hidden = !(shown === 0 && active !== 'all' && panels.length > 0);
        if (filters) filters.querySelectorAll('.cx-filter').forEach(b => b.classList.toggle('is-on', b.dataset.filter === active));
        panels.forEach(p => io.observe(p));
        if (first) requestAnimationFrame(() => first.classList.add('is-in'));
        if (designMode) panels.forEach(p => p.classList.add('is-in'));
      }

      if (filters) filters.addEventListener('click', (e) => {
        const btn = e.target.closest('.cx-filter');
        if (!btn) return;
        active = btn.dataset.filter;
        history.replaceState(null, '', active === 'all' ? location.pathname + location.search : `#${active}`);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        render();
      });
      host.querySelectorAll('[data-show-all]').forEach(b => b.addEventListener('click', () => {
        active = 'all';
        render();
      }));
      window.addEventListener('hashchange', () => {
        const next = valid.includes(location.hash.replace('#', '')) ? location.hash.replace('#', '') : 'all';
        if (next === active) return;
        active = next;
        window.scrollTo(0, 0);
        render();
      });

      render();

      // Scroll-snap only switches on once the page sits at the top, otherwise
      // the browser snaps straight to the last panel on load. Not in the editor.
      if (!designMode && !document.body.classList.contains('is-ready')) {
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        window.scrollTo(0, 0);
        const enableSnap = () => {
          if (document.body.classList.contains('is-ready')) return;
          if (window.scrollY < 5) window.scrollTo(0, 0);
          document.body.classList.add('is-ready');
        };
        requestAnimationFrame(enableSnap);
        setTimeout(enableSnap, 400);
      }
    });
  }

  /* ---------- Generic tag filter (events list, store grid) ---------- */
  function matchTag(filter, tag) {
    if (filter === 'all') return true;
    const f = (filter || '').toLowerCase().trim();
    const t = (tag || '').toLowerCase().trim();
    return !!t && (f === t || f.startsWith(t) || t.startsWith(f));
  }

  function initFilters(root) {
    root.querySelectorAll('[data-filter-group]').forEach(host => {
      const bar = host.querySelector('[data-filter-bar]');
      const items = Array.from(host.querySelectorAll('[data-filter-item]'));
      const count = host.querySelector('[data-filter-count]');
      const one = host.dataset.labelOne || 'item';
      const many = host.dataset.labelMany || 'items';
      if (!bar) return;
      const apply = (value) => {
        let shown = 0;
        items.forEach(it => {
          const on = matchTag(value, it.dataset.filterItem);
          it.hidden = !on;
          if (on) shown++;
        });
        bar.querySelectorAll('[data-filter]').forEach(b => {
          const on = b.dataset.filter === value;
          b.classList.toggle('btn-primary', on);
          b.classList.toggle('btn-outline', !on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        if (count) count.textContent = `${shown} ${shown === 1 ? one : many}`;
      };
      bar.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-filter]');
        if (btn) apply(btn.dataset.filter);
      });
      apply('all');
    });
  }

  /* ---------- Product page: gallery + variant picker ---------- */
  function initProduct(root) {
    root.querySelectorAll('[data-product]').forEach(host => {
      const main = host.querySelector('.pdp-main img');
      const thumbs = host.querySelector('.pdp-thumbs');
      if (thumbs && main) thumbs.addEventListener('click', (e) => {
        const b = e.target.closest('.pdp-thumb');
        if (!b) return;
        thumbs.querySelectorAll('.pdp-thumb').forEach(x => x.classList.remove('is-active'));
        b.classList.add('is-active');
        main.src = b.dataset.src;
        if (b.dataset.srcset) main.srcset = b.dataset.srcset;
      });

      const dataEl = host.querySelector('[data-variants]');
      if (!dataEl) return;
      let variants = [];
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }
      const form = host.querySelector('form[action*="/cart/add"]');
      const idInput = form && form.querySelector('input[name="id"]');
      const buy = host.querySelector('[data-pdp-buy]');
      const priceEl = host.querySelector('[data-pdp-price]');
      const compareEl = host.querySelector('[data-pdp-compare]');
      const groups = Array.from(host.querySelectorAll('[data-option-index]'));

      function selected() {
        return groups.map(g => {
          const c = g.querySelector('input:checked');
          return c ? c.value : null;
        });
      }
      function update() {
        const opts = selected();
        const v = variants.find(x => x.options.every((o, i) => opts[i] === null || o === opts[i]));
        groups.forEach(g => {
          const i = +g.dataset.optionIndex;
          g.querySelectorAll('input').forEach(inp => {
            inp.closest('label').classList.toggle('is-selected', inp.checked);
            const test = opts.slice();
            test[i] = inp.value;
            const exists = variants.some(x => x.available && x.options.every((o, k) => o === test[k]));
            inp.closest('label').classList.toggle('is-unavailable', !exists);
          });
        });
        if (!v) {
          if (buy) { buy.disabled = true; buy.textContent = buy.dataset.labelUnavailable; }
          return;
        }
        if (idInput) idInput.value = v.id;
        if (priceEl) priceEl.textContent = v.price;
        if (compareEl) { compareEl.textContent = v.compare || ''; compareEl.hidden = !v.compare; }
        if (buy) {
          buy.disabled = !v.available;
          buy.textContent = v.available ? buy.dataset.labelAdd : buy.dataset.labelSoldout;
        }
        if (v.image && main) { main.src = v.image; main.removeAttribute('srcset'); }
        if (!designMode) {
          const url = new URL(location.href);
          url.searchParams.set('variant', v.id);
          history.replaceState(null, '', url);
        }
      }
      groups.forEach(g => g.addEventListener('change', update));
      update();
    });
  }

  /* ---------- Cart page: quantity changes submit the form ---------- */
  function initCart(root) {
    root.querySelectorAll('[data-cart-form]').forEach(form => {
      form.querySelectorAll('input[type="number"]').forEach(inp => {
        inp.addEventListener('change', () => form.requestSubmit ? form.requestSubmit() : form.submit());
      });
    });
  }

  /* ---------- Coarse-pointer class cards: first tap reveals, second follows ---------- */
  document.addEventListener('click', (e) => {
    if (!coarse()) return;
    const card = e.target.closest('.class-card, .ctile');
    document.querySelectorAll('.class-card.is-active, .ctile.is-active').forEach(c => { if (c !== card) c.classList.remove('is-active'); });
    if (!card) return;
    if (!card.classList.contains('is-active')) { e.preventDefault(); card.classList.add('is-active'); }
  });

  function initAll(root) {
    initHeader(root);
    initCompass(root);
    initClasses(root);
    initFilters(root);
    initProduct(root);
    initCart(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document));
  else initAll(document);

  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', closeTakeover);
  document.addEventListener('shopify:block:select', (e) => {
    const btn = e.target.closest && e.target.closest('[data-dir]');
    const host = btn && btn.closest('[data-compass]');
    if (btn && host) showTakeover(btn, host);
    const panel = e.target.closest && e.target.closest('.cx-panel');
    if (panel) { panel.hidden = false; panel.classList.add('is-in'); }
  });
  document.addEventListener('shopify:block:deselect', closeTakeover);
})();
